import { Button } from 'app/components/ui/button'
import { SmsTemplatePicker } from 'app/components/sms-templates/TemplatePicker'
import { toEngineText } from 'app/lib/messageTemplates'
import { useAuthStore } from 'app/stores/StoreContext'
import { NOTIFY_CHANNELS } from 'app/lib/workflows/actionCatalogs'
import type {
  AiAgentSmsNodeData,
  InitiateCallNodeData,
  NotificationChannel,
  SendNotificationNodeData,
  SendSmsNodeData,
  WorkflowSettings,
} from 'app/types/workflows'
import {
  availablePhoneFields,
  type ContactField,
  fieldsToVariables,
  groupFieldsBySource,
} from 'app/lib/workflows/dataContext'
import type { NodeErrors } from 'app/lib/workflows/validation'
import { categorySummary, sendWindowSummary } from 'app/lib/workflows/deliverySummary'
import { workflowCategory } from 'app/lib/workflows/workflowSettings'
import { fieldGroupChoices, type Panel } from './panelShared'
import { ChoiceSelect, Field, MessageField, MultiSelect, TextField, VariableTextField } from './fields'
import { Section } from './Section'
import { DURATION_INPUT, optionChoices, useUserOptions } from './panelShared'
import { NEW_TEXT_CONVERSATION, type TriggerKind } from 'app/lib/workflows/triggerCatalog'

export function SendSmsPanel({
  data,
  set,
  errors,
  settings,
  onEditSettings,
  availableFields,
  sample,
}: Panel<SendSmsNodeData> & {
  /** Workflow-level texting policy, read back here and edited in the workflow settings panel. */
  settings: WorkflowSettings
  onEditSettings: () => void
  availableFields: string[]
  sample?: Record<string, string>
}) {
  const phoneFields = availablePhoneFields(availableFields)
  const toMode = data.toMode ?? `field`
  const authStore = useAuthStore()
  // A message sent to the number that called is the one we send to people who have never opted in.
  // It is allowed because it is a REPLY to their own call rather than marketing, and that distinction
  // lives entirely in the wording. One sentence about a discount changes what the message legally is.
  // So the practice gets the workflow and an admin owns the sentence. Keyed on the recipient rather
  // than on which template it came from, so a hand-built copy is covered too.
  const lockedReply = data.toField === `callerPhone` && !authStore.isSuper

  return (
    <>
      <Section title="Send to">
        <Field label="Recipient">
          <ChoiceSelect
            value={toMode}
            choices={[
              { value: `field`, label: `The contact's number` },
              { value: `custom`, label: `A custom number` },
            ]}
            onChange={(v) => set({ toMode: v })}
          />
        </Field>
        {toMode === `custom` ? (
          <Field
            label="Number"
            error={errors.toNumber}
            info="A fixed number, such as yourself or one staff member. Needs backend support before it can go live."
          >
            <TextField
              value={data.toNumber ?? ``}
              placeholder="+1 555 123 4567"
              invalid={!!errors.toNumber}
              onChange={(e) => set({ toNumber: e.target.value })}
            />
          </Field>
        ) : (
          <PhoneNumberField
            phoneFields={phoneFields}
            value={data.toField}
            errors={errors}
            onChange={(v) => set({ toField: v })}
          />
        )}
      </Section>
      <Section title="Message">
        {!lockedReply && (
          <div className="mb-1 flex justify-end text-xs">
            <SmsTemplatePicker
              trigger={
                <Button variant="link" size="inline">
                  Use template
                </Button>
              }
              // No currentBody here on purpose: a workflow message is in the
              // engine's flat vocabulary (plus trigger-scoped fields like
              // {{messageText}}), so capturing it as a template would leak
              // tokens the inbox/bulk surfaces can't resolve.
              onSelect={(t) => {
                // Normalizes legacy token spellings; fallbacks already sit in
                // the body in the engine's `{{key ?? "text"}}` grammar.
                const text = toEngineText(t.body)
                set({
                  message: data.message
                    ? `${data.message}
${text}`
                    : text,
                })
              }}
            />
          </div>
        )}
        <Field
          label="Message"
          error={errors.message}
          // Consequence, not background: the reader is looking at a field they cannot type into.
          hint={
            lockedReply
              ? `Locked. Its wording keeps this a reply rather than marketing; an admin can change it.`
              : undefined
          }
        >
          <MessageField
            value={data.message}
            onChange={(message) => set({ message })}
            error={errors.message}
            variables={fieldsToVariables(availableFields)}
            sample={sample}
            showSegments
            readOnly={lockedReply}
            placeholder="Write the text the contact will receive"
          />
        </Field>
      </Section>
      {/* Workflow-level policy is read back, never edited here (design language rule 6): every text
          this workflow sends shares one window and one category, so they live in one panel. */}
      <Section title="Delivery">
        <div className="space-y-1 text-xs text-default">
          <div className="flex items-start justify-between gap-3">
            <span>{sendWindowSummary(settings.sendTimes)}</span>
            <Button variant="link" size="xs" onClick={onEditSettings} className="h-auto shrink-0">
              Edit
            </Button>
          </div>
          <div>{categorySummary(workflowCategory(settings))}</div>
        </div>
        {/* Per STEP, unlike the shared window: one workflow mixes texts that keep overnight (a lead
            reply is good at 8 AM) with texts that go stale (a "we're calling you now" heads-up is
            nonsense by morning). Only the author knows which is which. Only offered while the
            window is on: with it off there is nothing to hold until, the engine skips late sends
            outright, and the read-back above already says so. */}
        {settings.sendTimes.restrict && (
          <Field
            label="Overnight sends"
            info="A text that comes due between 8 PM and 8 AM cannot go out. Hold it for the morning, or drop it if it would be stale by then. This choice is for this text only."
          >
            <ChoiceSelect
              value={data.nightSend ?? `hold`}
              choices={[
                { value: `hold`, label: `Hold until the window opens` },
                { value: `skip`, label: `Skip` },
              ]}
              onChange={(v) => set({ nightSend: v })}
            />
          </Field>
        )}
      </Section>
    </>
  )
}

function PhoneNumberField({
  phoneFields,
  value,
  errors,
  onChange,
}: {
  phoneFields: ContactField[]
  value: string
  errors: NodeErrors
  onChange: (value: string) => void
}) {
  if (phoneFields.length === 0) return null
  return (
    <Field label="Number" error={errors.toField ?? errors.phone}>
      <ChoiceSelect
        value={value}
        groups={fieldGroupChoices(groupFieldsBySource(phoneFields))}
        placeholder="Select a number"
        invalid={!!errors.toField}
        onChange={onChange}
      />
    </Field>
  )
}

/** The AI agent handoff. Night sends are deliberately NOT a field: every message this block is
 *  responsible for (the opener and the agent's own replies) waits with no per-step skip, so
 *  offering the choice Send SMS offers would misrepresent what happens. */
export function AiAgentSmsPanel({
  data,
  set,
  errors,
  settings,
  onEditSettings,
  availableFields,
  sample,
  triggerKind,
}: Panel<AiAgentSmsNodeData> & {
  /** Workflow-level texting policy, read back here and edited in the workflow settings panel. */
  settings: WorkflowSettings
  onEditSettings: () => void
  availableFields: string[]
  sample?: Record<string, string>
  triggerKind?: TriggerKind
}) {
  const phoneFields = availablePhoneFields(availableFields)
  const variables = fieldsToVariables(availableFields)
  const whenBusy = data.whenBusy ?? `skip`
  // The patient texted first, so the agent answers them: nothing to open with, nowhere to pick.
  const replying = triggerKind === NEW_TEXT_CONVERSATION
  return (
    <>
      {replying ? (
        <Section title="Reply to their text">
          <div className="space-y-1 text-sm text-subtle">
            <div>Replies to the number that texted in.</div>
            <div>The agent reads their text and replies to it.</div>
          </div>
        </Section>
      ) : (
        <Section title="Start the conversation">
          <PhoneNumberField
            phoneFields={phoneFields}
            value={data.toField}
            errors={errors}
            onChange={(v) => set({ toField: v })}
          />
          <Field
            label="Opening message"
            error={errors.message}
            info="The only message you write. The agent takes the conversation from the reply onward."
          >
            <MessageField
              value={data.message}
              onChange={(message) => set({ message })}
              error={errors.message}
              variables={variables}
              sample={sample}
              showSegments
              placeholder="Write the text that opens the conversation…"
            />
          </Field>
        </Section>
      )}

      <Section title="Brief the agent">
        <Field
          label="Brief"
          error={errors.brief}
          info="Why you are reaching out, and what counts as a win. The agent reads the last 30 days of this conversation too, so it will not repeat what staff have already said."
        >
          <MessageField
            value={data.brief}
            onChange={(brief) => set({ brief })}
            error={errors.brief}
            variables={variables}
            sample={sample}
            placeholder="e.g. Overdue for a cleaning. Goal: get them booked."
          />
        </Field>
      </Section>

      <Section title="If the agent is already talking">
        <Field label="This step">
          <ChoiceSelect
            value={whenBusy}
            choices={[
              { value: `skip`, label: `Nothing, leave the conversation alone` },
              { value: `takeOver`, label: `Take over with this brief` },
            ]}
            onChange={(v) => set({ whenBusy: v as `takeOver` | `skip` })}
          />
        </Field>
        {whenBusy === `skip` ? (
          <>
            <div className="text-[11px] text-subtle">No text sends and the brief is not added.</div>
            {/* A conversation nobody has touched in weeks is not really "busy". Without this, a
                recall workflow stays permanently locked out by a months-old exchange. */}
            <Field
              label="Unless quiet for"
              info="Leave blank to always stand down. With a number, an older conversation counts as cold and this step takes over instead."
            >
              <div className="flex items-center gap-2">
                <div className="w-16">
                  <TextField
                    type="number"
                    min={1}
                    className={DURATION_INPUT}
                    value={data.skipIfAgentSpokeWithinDays == null ? `` : String(data.skipIfAgentSpokeWithinDays)}
                    placeholder="7"
                    onChange={(e) =>
                      set({
                        skipIfAgentSpokeWithinDays: e.target.value === `` ? undefined : Number(e.target.value),
                      })
                    }
                  />
                </div>
                <span className="text-xs text-subtle">days since the agent last spoke</span>
              </div>
            </Field>
          </>
        ) : (
          <div className="text-[11px] text-subtle">
            {replying
              ? `This brief replaces the agent's current one.`
              : `The opening message sends and this brief replaces the agent's current one.`}
          </div>
        )}
      </Section>

      {/* No follow-up editor here on purpose: a nudge ladder is canvas structure, built as
          Wait for reply with the nudge on its no-reply path. Saying so beats leaving an author to
          wonder where chasing went, and the canvas version can also do a task or a call. */}
      <Section title="No response">
        <div className="text-xs text-subtle">
          Add a <span className="font-medium text-default">Wait for reply</span> step after this one and put the nudge
          on its no-reply path.
        </div>
      </Section>
      {/* The window governs the opener too: verified on dev 2026-10-07, an out-of-window opener parked
          the run (`waitingFor: send_window`). With the window off, sendWindowSummary's "late sends are
          skipped" is Send SMS behavior; the agent holds through quiet hours instead. */}
      <Section title="Delivery">
        <div className="space-y-1 text-xs text-default">
          <div className="flex items-start justify-between gap-3">
            <span>{settings.sendTimes.restrict ? sendWindowSummary(settings.sendTimes) : `Any time`}</span>
            <Button variant="link" size="xs" onClick={onEditSettings} className="h-auto shrink-0">
              Edit
            </Button>
          </div>
          <div>{categorySummary(workflowCategory(settings))}</div>
          <div className="text-subtle">
            {replying
              ? `Quiet hours always apply: the agent's replies wait until morning.`
              : settings.sendTimes.restrict
                ? `Outside the window, the opening message waits until it opens. The agent's replies wait until morning during quiet hours.`
                : `Quiet hours always apply: the opening message and the agent's replies wait until morning.`}
          </div>
        </div>
      </Section>
    </>
  )
}

export function InitiateCallPanel({
  data,
  set,
  errors,
  availableFields,
  sample,
}: Panel<InitiateCallNodeData> & { availableFields: string[]; sample?: Record<string, string> }) {
  return (
    <>
      <Section>
        <Field
          label="Number to ring"
          error={errors.ringNumber}
          info="Rings this number first, then bridges the call to the contact. Caller ID is the location's tracking number."
        >
          <TextField
            value={data.ringNumber ?? ``}
            invalid={!!errors.ringNumber}
            placeholder="(207) 555-0142"
            onChange={(e) => set({ ringNumber: e.target.value })}
          />
        </Field>
        <Field
          label="Message to the contact"
          info="Optional. Read aloud to the contact when they answer, right before they're joined with staff, so write it for the patient's ears. Blank means the call starts like a normal call. Staff always hear CallRail's “Press 1 to connect” prompt."
        >
          <VariableTextField
            value={data.whisper ?? ``}
            onChange={(whisper) => set({ whisper: whisper || undefined })}
            variables={fieldsToVariables(availableFields)}
            sample={sample}
            // The example must model the NAMESPACED token: bare {{firstName}} resolves to nothing
            // at the engine and the serializer blocks it, so the placeholder can't teach it.
            placeholder="Hi {{contact.firstName}}, this is your dental office calling about your appointment"
          />
        </Field>
      </Section>
    </>
  )
}

export function SendNotificationPanel({
  data,
  set,
  errors,
  availableFields,
  sample,
}: Panel<SendNotificationNodeData> & { availableFields: string[]; sample?: Record<string, string> }) {
  // workspaceOnly: notifications only surface for workspace users — offering org/super staff here
  // would dispatch into a void (execution-audit F1).
  const recipients = useUserOptions({
    tokens: [
      { id: `owner`, name: `Contact owner` },
      { id: `team`, name: `Whole team` },
    ],
    workspaceOnly: true,
  })
  return (
    <>
      <Section title="Send to">
        <Field label="Channel">
          <ChoiceSelect
            value={data.channel}
            choices={NOTIFY_CHANNELS}
            onChange={(v) => set({ channel: v as NotificationChannel })}
          />
        </Field>
        <Field
          label="Recipients"
          error={errors.recipients}
          info="Workspace staff only: notifications don’t surface for org-level or Trackable users."
        >
          <MultiSelect
            choices={optionChoices(recipients)}
            selected={data.recipients}
            onChange={(recipients) => set({ recipients })}
            placeholder="Select recipients…"
            invalid={!!errors.recipients}
          />
        </Field>
      </Section>
      <Section title="Message">
        <Field label="Message" error={errors.message}>
          <MessageField
            value={data.message}
            onChange={(message) => set({ message })}
            error={errors.message}
            variables={fieldsToVariables(availableFields)}
            sample={sample}
            rows={4}
            placeholder="What should the notification say?"
          />
        </Field>
      </Section>
    </>
  )
}
